import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Output, Input, Component } from '@angular/core';
import * as i1 from '@angular/forms';
import { FormGroup, ReactiveFormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ColorPickerDirective, ColorPickerComponent } from 'ngx-color-picker';
import { Subject, takeUntil } from 'rxjs';

var ColorMode;
(function (ColorMode) {
    ColorMode["Color"] = "color";
    ColorMode["Grayscale"] = "grayscale";
    ColorMode["Presets"] = "presets";
})(ColorMode || (ColorMode = {}));
var ColorOutputFormat;
(function (ColorOutputFormat) {
    ColorOutputFormat["Auto"] = "auto";
    ColorOutputFormat["Hex"] = "hex";
    ColorOutputFormat["Rgba"] = "rgba";
    ColorOutputFormat["Hsla"] = "hsla";
})(ColorOutputFormat || (ColorOutputFormat = {}));
var ColorDialogPosition;
(function (ColorDialogPosition) {
    ColorDialogPosition["Auto"] = "auto";
    ColorDialogPosition["Top"] = "top";
    ColorDialogPosition["Bottom"] = "bottom";
    ColorDialogPosition["Left"] = "left";
    ColorDialogPosition["Right"] = "right";
    ColorDialogPosition["TopLeft"] = "top-left";
    ColorDialogPosition["TopRight"] = "top-right";
    ColorDialogPosition["BottomLeft"] = "bottom-left";
    ColorDialogPosition["BottomRight"] = "bottom-right";
})(ColorDialogPosition || (ColorDialogPosition = {}));
var ColorAlphaMode;
(function (ColorAlphaMode) {
    ColorAlphaMode["Enabled"] = "enabled";
    ColorAlphaMode["Disabled"] = "disabled";
    ColorAlphaMode["Always"] = "always";
    ColorAlphaMode["Forced"] = "forced";
})(ColorAlphaMode || (ColorAlphaMode = {}));

/**
 * Wrapper component for ngx-color-picker.
 * Reference: https://github.com/zefoy/ngx-color-picker
 */
class E11ColorPickerComponent {
    constructor() {
        /** Color to show in the dialog and input */
        this.color = '#ffffff';
        /** Name of color picker input */
        this.name = 'form-color-picker';
        /** Dialog color mode */
        this.colorMode = ColorMode.Color;
        /** Display color value in input field */
        this.displayColor = false;
        /** Output color format */
        this.colorOutputFormat = ColorOutputFormat.Auto;
        /** Dialog position */
        this.colorDialogPosition = ColorDialogPosition.Auto;
        /** Disables the opening of the dialog */
        this.disabled = false;
        /** Sets the default open / close state of the color picker */
        this.defaultOpen = false;
        /** Color alpha mode */
        this.alphaMode = ColorAlphaMode.Enabled;
        /** Used when color is undefined or not well-formed */
        this.fallbackColor = '#000';
        /** Dialog offset percentage relative to the directive element */
        this.dialogPositionOffset = '0%';
        /** Dialog position is calulated relative to dialog arrow */
        this.dialogPositionRelativeToArrow = false;
        /** Label text for the preset colors if any are provided */
        this.presetLabel = 'Preset Colors';
        /** Array of preset colors to show in the color picker dialog */
        this.presetColors = [];
        /** Disables/hides the color input field from the dialog */
        this.disableColorInput = false;
        /** Disables the eye dropper from the dialog */
        this.disableEyeDropper = false;
        /** Dialog positioning mode */
        this.dialogDisplayMode = 'popup';
        /** Save currently selected color when user clicks outside */
        this.saveOnClickOutside = true;
        /** Close the dialog when user clicks outside */
        this.closeOnClickOutside = true;
        /** Show an OK button which saves the color */
        this.showOkButton = false;
        /** Show a Cancel button which resets the color */
        this.showCancelButton = false;
        /** Show an Add Color button which adds the color into preset */
        this.showAddColorButton = false;
        /** Additional class for customizing the Remove Color button */
        this.removeColorButtonClass = '';
        /** Additional class for customizing the Preset Colors container */
        this.presetColorsClass = '';
        /** Message for empty colors if any provided used */
        this.presetEmptyMessage = 'No colors added';
        /** Additional class for customizing the empty colors message */
        this.presetEmptyMessageClass = '';
        /** Array of HTML elements that will be ignored when clicked */
        this.ignoredElements = [];
        /** Parent form */
        this.parentForm = new FormGroup({});
        /** Current color value when dialog is opened */
        this.colorPickerOpen = new EventEmitter();
        /** Current color value when dialog is closed */
        this.colorPickerClosed = new EventEmitter();
        /** Changed color value */
        this.colorPickerChange = new EventEmitter();
        /** Color select canceled, send when Cancel button is pressed */
        this.colorPickerCancel = new EventEmitter();
        /** Selected color value, when OK button is pressed */
        this.colorPickerSelect = new EventEmitter();
        /** Status of the dialog, send when dialog is opened / closed */
        this.dialogToggleOpenChange = new EventEmitter();
        /** Input name and its value, send when user changes color through inputs */
        this.dialogInputChange = new EventEmitter();
        /** Preset colors, send when 'Add Color' button is pressed */
        this.presetColorsChange = new EventEmitter();
        this.hasError = false;
        this.destroy$ = new Subject();
        this.value = '';
        this.onChange = (_) => { };
        this.onTouched = () => { };
    }
    ngOnInit() {
        this.initFormMethods();
    }
    onValidate(formControl) {
        if (formControl.errors && (formControl.touched || formControl.dirty)) {
            this.hasError = true;
        }
        else {
            this.hasError = false;
        }
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    ngOnDestroy() {
        if (this.destroy$ && this.destroy$.next) {
            this.destroy$.next(true);
            this.destroy$.unsubscribe();
        }
    }
    handleOnColorPickerChange(color) {
        this.updateFormValue(color);
        this.colorPickerChange.emit(color);
    }
    updateFormValue(value) {
        if (this.formControlName && this.parentForm.controls[this.formControlName]) {
            this.parentForm.controls[this.formControlName].setValue(value);
        }
    }
    initFormMethods() {
        if (!this.formControlName || !this.parentForm)
            return;
        const formControl = this.parentForm.controls[this.formControlName];
        if (!formControl)
            return;
        if (this.parentForm.dirty && formControl?.errors)
            this.hasError = true;
        formControl?.valueChanges?.pipe(takeUntil(this.destroy$))?.subscribe(() => {
            this.onValidate(formControl);
        });
        formControl.updateValueAndValidity();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ColorPickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ColorPickerComponent, isStandalone: true, selector: "e11-color-picker", inputs: { color: "color", name: "name", inputClassOverrides: "inputClassOverrides", containerClassOverrides: "containerClassOverrides", colorMode: "colorMode", displayColor: "displayColor", colorOutputFormat: "colorOutputFormat", colorDialogPosition: "colorDialogPosition", dialogHeight: "dialogHeight", dialogWidth: "dialogWidth", disabled: "disabled", defaultOpen: "defaultOpen", alphaMode: "alphaMode", fallbackColor: "fallbackColor", dialogPositionOffset: "dialogPositionOffset", dialogPositionRelativeToArrow: "dialogPositionRelativeToArrow", presetLabel: "presetLabel", presetColors: "presetColors", disableColorInput: "disableColorInput", disableEyeDropper: "disableEyeDropper", dialogDisplayMode: "dialogDisplayMode", saveOnClickOutside: "saveOnClickOutside", closeOnClickOutside: "closeOnClickOutside", showOkButton: "showOkButton", okButtonText: "okButtonText", showCancelButton: "showCancelButton", cancelButtonText: "cancelButtonText", showAddColorButton: "showAddColorButton", addColorButtonText: "addColorButtonText", removeColorButtonClass: "removeColorButtonClass", presetColorsClass: "presetColorsClass", maxPresetColors: "maxPresetColors", presetEmptyMessage: "presetEmptyMessage", presetEmptyMessageClass: "presetEmptyMessageClass", ignoredElements: "ignoredElements", formControlName: "formControlName", parentForm: "parentForm" }, outputs: { colorPickerOpen: "colorPickerOpen", colorPickerClosed: "colorPickerClosed", colorPickerChange: "colorPickerChange", colorPickerCancel: "colorPickerCancel", colorPickerSelect: "colorPickerSelect", dialogToggleOpenChange: "dialogToggleOpenChange", dialogInputChange: "dialogInputChange", presetColorsChange: "presetColorsChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11ColorPickerComponent),
                multi: true,
            },
        ], ngImport: i0, template: `
    <div class="color-input-container e11-p-1 e11-bg-gray-200 e11-w-fit {{ containerClassOverrides }}" [formGroup]="parentForm">
      <input
        #inputRef
        readonly
        [cpAddColorButton]="showAddColorButton"
        [cpAddColorButtonText]="addColorButtonText ?? 'Add Color'"
        [cpAlphaChannel]="alphaMode"
        [cpCancelButton]="showCancelButton"
        [cpCancelButtonText]="cancelButtonText ?? 'Cancel'"
        [cpCloseClickOutside]="closeOnClickOutside"
        [cpColorMode]="colorMode"
        [cpDialogDisplay]="dialogDisplayMode"
        [cpDisabled]="disabled"
        [cpDisableInput]="disableColorInput"
        [cpEyeDropper]="!disableEyeDropper"
        [cpFallbackColor]="fallbackColor"
        [cpIgnoredElements]="ignoredElements"
        [cpMaxPresetColorsLength]="maxPresetColors ?? null"
        [cpOKButton]="showOkButton"
        [cpOKButtonText]="okButtonText ?? 'OK'"
        [cpOutputFormat]="colorOutputFormat"
        [cpPosition]="colorDialogPosition"
        [cpPositionOffset]="dialogPositionOffset"
        [cpPositionRelativeToArrow]="dialogPositionRelativeToArrow"
        [cpPresetLabel]="presetLabel"
        [cpPresetColors]="presetColors"
        [cpPresetColorsClass]="presetColorsClass"
        [cpPresetEmptyMessage]="presetEmptyMessage"
        [cpPresetEmptyMessageClass]="presetEmptyMessageClass"
        [cpSaveClickOutside]="saveOnClickOutside"
        [cpToggle]="defaultOpen"
        [cpHeight]="dialogHeight"
        [cpWidth]="dialogWidth"
        [(colorPicker)]="color"
        [formControlName]="formControlName"
        [name]="name"
        [style.background]="color"
        [value]="displayColor ? color : ''"
        (colorPickerOpen)="colorPickerOpen.emit($event)"
        (colorPickerClose)="colorPickerClosed.emit($event)"
        (colorPickerChange)="handleOnColorPickerChange($event)"
        (colorPickerCancel)="colorPickerCancel.emit()"
        (colorPickerSelect)="colorPickerSelect.emit($event)"
        (cpToggleChange)="dialogToggleOpenChange.emit($event)"
        (cpInputChange)="dialogInputChange.emit($event)"
        (cpPresetColorsChange)="presetColorsChange.emit($event)"
        class="e11-border-gray-500 e11-border-solid e11-text-transparent e11-caret-transparent e11-cursor-default {{ inputClassOverrides }}"
      />
    </div>
  `, isInline: true, styles: [".color-input-container{border-radius:2px}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: ColorPickerDirective, selector: "[colorPicker]", inputs: ["colorPicker", "cpWidth", "cpHeight", "cpToggle", "cpDisabled", "cpIgnoredElements", "cpFallbackColor", "cpColorMode", "cpCmykEnabled", "cpOutputFormat", "cpAlphaChannel", "cpDisableInput", "cpDialogDisplay", "cpSaveClickOutside", "cpCloseClickOutside", "cpUseRootViewContainer", "cpPosition", "cpPositionOffset", "cpPositionRelativeToArrow", "cpOKButton", "cpOKButtonText", "cpOKButtonClass", "cpCancelButton", "cpCancelButtonText", "cpCancelButtonClass", "cpEyeDropper", "cpPresetLabel", "cpPresetColors", "cpPresetColorsClass", "cpMaxPresetColorsLength", "cpPresetEmptyMessage", "cpPresetEmptyMessageClass", "cpAddColorButton", "cpAddColorButtonText", "cpAddColorButtonClass", "cpRemoveColorButtonClass", "cpArrowPosition", "cpExtraTemplate"], outputs: ["cpInputChange", "cpToggleChange", "cpSliderChange", "cpSliderDragEnd", "cpSliderDragStart", "colorPickerOpen", "colorPickerClose", "colorPickerCancel", "colorPickerSelect", "colorPickerChange", "cpCmykColorChange", "cpPresetColorsChange"], exportAs: ["ngxColorPicker"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ColorPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-color-picker', standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11ColorPickerComponent),
                            multi: true,
                        },
                    ], template: `
    <div class="color-input-container e11-p-1 e11-bg-gray-200 e11-w-fit {{ containerClassOverrides }}" [formGroup]="parentForm">
      <input
        #inputRef
        readonly
        [cpAddColorButton]="showAddColorButton"
        [cpAddColorButtonText]="addColorButtonText ?? 'Add Color'"
        [cpAlphaChannel]="alphaMode"
        [cpCancelButton]="showCancelButton"
        [cpCancelButtonText]="cancelButtonText ?? 'Cancel'"
        [cpCloseClickOutside]="closeOnClickOutside"
        [cpColorMode]="colorMode"
        [cpDialogDisplay]="dialogDisplayMode"
        [cpDisabled]="disabled"
        [cpDisableInput]="disableColorInput"
        [cpEyeDropper]="!disableEyeDropper"
        [cpFallbackColor]="fallbackColor"
        [cpIgnoredElements]="ignoredElements"
        [cpMaxPresetColorsLength]="maxPresetColors ?? null"
        [cpOKButton]="showOkButton"
        [cpOKButtonText]="okButtonText ?? 'OK'"
        [cpOutputFormat]="colorOutputFormat"
        [cpPosition]="colorDialogPosition"
        [cpPositionOffset]="dialogPositionOffset"
        [cpPositionRelativeToArrow]="dialogPositionRelativeToArrow"
        [cpPresetLabel]="presetLabel"
        [cpPresetColors]="presetColors"
        [cpPresetColorsClass]="presetColorsClass"
        [cpPresetEmptyMessage]="presetEmptyMessage"
        [cpPresetEmptyMessageClass]="presetEmptyMessageClass"
        [cpSaveClickOutside]="saveOnClickOutside"
        [cpToggle]="defaultOpen"
        [cpHeight]="dialogHeight"
        [cpWidth]="dialogWidth"
        [(colorPicker)]="color"
        [formControlName]="formControlName"
        [name]="name"
        [style.background]="color"
        [value]="displayColor ? color : ''"
        (colorPickerOpen)="colorPickerOpen.emit($event)"
        (colorPickerClose)="colorPickerClosed.emit($event)"
        (colorPickerChange)="handleOnColorPickerChange($event)"
        (colorPickerCancel)="colorPickerCancel.emit()"
        (colorPickerSelect)="colorPickerSelect.emit($event)"
        (cpToggleChange)="dialogToggleOpenChange.emit($event)"
        (cpInputChange)="dialogInputChange.emit($event)"
        (cpPresetColorsChange)="presetColorsChange.emit($event)"
        class="e11-border-gray-500 e11-border-solid e11-text-transparent e11-caret-transparent e11-cursor-default {{ inputClassOverrides }}"
      />
    </div>
  `, imports: [CommonModule, ReactiveFormsModule, ColorPickerComponent, ColorPickerDirective], styles: [".color-input-container{border-radius:2px}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }], name: [{
                type: Input
            }], inputClassOverrides: [{
                type: Input
            }], containerClassOverrides: [{
                type: Input
            }], colorMode: [{
                type: Input
            }], displayColor: [{
                type: Input
            }], colorOutputFormat: [{
                type: Input
            }], colorDialogPosition: [{
                type: Input
            }], dialogHeight: [{
                type: Input
            }], dialogWidth: [{
                type: Input
            }], disabled: [{
                type: Input
            }], defaultOpen: [{
                type: Input
            }], alphaMode: [{
                type: Input
            }], fallbackColor: [{
                type: Input
            }], dialogPositionOffset: [{
                type: Input
            }], dialogPositionRelativeToArrow: [{
                type: Input
            }], presetLabel: [{
                type: Input
            }], presetColors: [{
                type: Input
            }], disableColorInput: [{
                type: Input
            }], disableEyeDropper: [{
                type: Input
            }], dialogDisplayMode: [{
                type: Input
            }], saveOnClickOutside: [{
                type: Input
            }], closeOnClickOutside: [{
                type: Input
            }], showOkButton: [{
                type: Input
            }], okButtonText: [{
                type: Input
            }], showCancelButton: [{
                type: Input
            }], cancelButtonText: [{
                type: Input
            }], showAddColorButton: [{
                type: Input
            }], addColorButtonText: [{
                type: Input
            }], removeColorButtonClass: [{
                type: Input
            }], presetColorsClass: [{
                type: Input
            }], maxPresetColors: [{
                type: Input
            }], presetEmptyMessage: [{
                type: Input
            }], presetEmptyMessageClass: [{
                type: Input
            }], ignoredElements: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], colorPickerOpen: [{
                type: Output
            }], colorPickerClosed: [{
                type: Output
            }], colorPickerChange: [{
                type: Output
            }], colorPickerCancel: [{
                type: Output
            }], colorPickerSelect: [{
                type: Output
            }], dialogToggleOpenChange: [{
                type: Output
            }], dialogInputChange: [{
                type: Output
            }], presetColorsChange: [{
                type: Output
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { ColorAlphaMode, ColorDialogPosition, ColorMode, ColorOutputFormat, E11ColorPickerComponent };
